import { moduleRegistry, useAuth } from '@ddone/auth';
import {
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  useTheme
} from '@ddone/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Bell,
  ChevronDown,
  Command,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  Plus,
  Search,
  Sun,
  Puzzle,
  UserRound,
  X
} from 'lucide-react';
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { navigation, navigationForAccess } from './navigation';
import { CommandPalette } from './command-palette';
import { ActivityDrawer } from './activity-drawer';
import { ActiveTimerPill } from '../components/active-timer';
import { ModuleFieldHelpProvider, RouteGuideHelp } from '../help/contextual-guide';

export function AppShell({ children }: { children: ReactNode }) {
  const { session, hasPermission, logout, switchWorkspace } = useAuth();
  const { preference, setPreference } = useTheme();
  const location = useRouterState({ select: (state) => state.location });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const commandShortcut =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
      ? '⌘ K'
      : 'Ctrl K';
  const workspaceLayout = location.pathname === '/notes' || location.pathname.startsWith('/notes/');
  const pluginModules = useSyncExternalStore(
    (listener) => moduleRegistry.subscribe(listener),
    moduleRegistry.snapshot
  );
  const visibleNavigation = useMemo(() => {
    const builtIn = navigationForAccess(
      navigation,
      session.modules,
      hasPermission,
      Boolean(session.user?.superAdmin)
    );
    const dynamic = pluginModules.flatMap((module) =>
      (module.navigation ?? [])
        .filter((item) => hasPermission(item.permission))
        .map((item) => ({
          id: `plugin:${module.id}:${item.id}`,
          label: item.label,
          href: item.href,
          moduleId: module.id,
          permission: item.permission,
          icon: item.icon ?? Puzzle,
          section: 'workspace' as const
        }))
    );
    return [...builtIn, ...dynamic];
  }, [hasPermission, pluginModules, session.modules, session.user?.superAdmin]);

  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const sidebar = (
    <>
      <div className="dd-sidebar-brand">
        <Link to="/" className="dd-logo" aria-label="DDone panoramica">
          <span>D</span>Done
        </Link>
        <IconButton
          className="dd-mobile-only"
          variant="ghost"
          aria-label="Chiudi menu"
          onClick={() => setMobileOpen(false)}
        >
          <X />
        </IconButton>
      </div>
      <Menu>
        <MenuTrigger asChild>
          <button type="button" className="dd-workspace-switcher">
            <span className="dd-workspace-mark">
              {session.activeWorkspace?.name.slice(0, 1).toUpperCase()}
            </span>
            <span>
              <strong>{session.activeWorkspace?.name}</strong>
              <small>Workspace attivo</small>
            </span>
            <ChevronDown size={16} />
          </button>
        </MenuTrigger>
        <MenuContent align="start" className="dd-workspace-menu">
          {session.workspaces.map((workspace) => (
            <MenuItem key={workspace.id} onSelect={() => void switchWorkspace(workspace)}>
              <span className="dd-workspace-mark">{workspace.name.slice(0, 1).toUpperCase()}</span>
              {workspace.name}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
      <nav className="dd-sidebar-nav" aria-label="Navigazione principale">
        <span className="dd-nav-label">Workspace</span>
        {visibleNavigation
          .filter((item) => item.section === 'workspace')
          .map((item) => (
            <Link
              key={item.id}
              to={item.href}
              activeOptions={{ exact: item.href === '/' }}
              className="dd-nav-item"
              activeProps={{ 'data-active': true }}
            >
              <item.icon size={19} aria-hidden="true" />
              <span>{item.label}</span>
            </Link>
          ))}
        {visibleNavigation.some((item) => item.section === 'system') ? (
          <span className="dd-nav-label dd-nav-label--spaced">Sistema</span>
        ) : null}
        {visibleNavigation
          .filter((item) => item.section === 'system')
          .map((item) => (
            <Link
              key={item.id}
              to={item.href}
              className="dd-nav-item"
              activeProps={{ 'data-active': true }}
            >
              <item.icon size={19} aria-hidden="true" />
              <span>{item.label}</span>
            </Link>
          ))}
      </nav>
      <div className="dd-sidebar-footer">
        <button
          type="button"
          className="dd-user-card"
          aria-label="Apri ricerca e azioni"
          onClick={() => setCommandOpen(true)}
        >
          <span className="dd-avatar">{initials(session.user?.displayName ?? '')}</span>
          <span>
            <strong>{session.user?.displayName}</strong>
            <small>{session.user?.email}</small>
          </span>
          <Command size={15} />
        </button>
      </div>
    </>
  );

  return (
    <div className="dd-app-shell">
      <aside className="dd-sidebar">{sidebar}</aside>
      {mobileOpen ? (
        <div
          className="dd-mobile-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      ) : null}
      <aside className="dd-mobile-sidebar" data-open={mobileOpen || undefined}>
        {sidebar}
      </aside>
      <div className="dd-main-column">
        <header className="dd-topbar">
          <IconButton
            className="dd-mobile-only"
            variant="ghost"
            aria-label="Apri menu"
            onClick={() => setMobileOpen(true)}
          >
            <MenuIcon />
          </IconButton>
          <button className="dd-command-trigger" type="button" onClick={() => setCommandOpen(true)}>
            <Search size={17} />
            <span>Cerca di cosa hai bisogno…</span>
            <kbd>{commandShortcut}</kbd>
          </button>
          <div className="dd-topbar-actions">
            <ActiveTimerPill />
            <RouteGuideHelp pathname={location.pathname} />
            <Menu>
              <MenuTrigger asChild>
                <IconButton variant="ghost" aria-label="Crea rapidamente">
                  <Plus />
                </IconButton>
              </MenuTrigger>
              <MenuContent align="end">
                {visibleNavigation
                  .filter((item) =>
                    [
                      'clients',
                      'crm',
                      'projects',
                      'notes',
                      'tasks',
                      'billing',
                      'expenses'
                    ].includes(item.id)
                  )
                  .map((item) => (
                    <MenuItem key={item.id} asChild>
                      <Link to={item.href} search={{ create: true } as never}>
                        {quickCreateLabel(item.id)}
                      </Link>
                    </MenuItem>
                  ))}
              </MenuContent>
            </Menu>
            <IconButton
              variant="ghost"
              aria-label="Apri registro modifiche"
              onClick={() => setActivityOpen(true)}
            >
              <Monitor />
            </IconButton>
            <IconButton variant="ghost" aria-label="Notifiche" asChild>
              <Link to="/notifications">
                <Bell />
              </Link>
            </IconButton>
            <Menu>
              <MenuTrigger asChild>
                <button type="button" className="dd-top-avatar" aria-label="Menu utente">
                  {initials(session.user?.displayName ?? '')}
                </button>
              </MenuTrigger>
              <MenuContent align="end">
                <MenuItem disabled>
                  <UserRound size={16} /> {session.user?.displayName}
                </MenuItem>
                <MenuSeparator />
                <MenuItem onSelect={() => setPreference('light')}>
                  <Sun size={16} /> Chiaro {preference === 'light' ? '✓' : ''}
                </MenuItem>
                <MenuItem onSelect={() => setPreference('dark')}>
                  <Moon size={16} /> Scuro {preference === 'dark' ? '✓' : ''}
                </MenuItem>
                <MenuItem onSelect={() => setPreference('system')}>
                  <Monitor size={16} /> Sistema {preference === 'system' ? '✓' : ''}
                </MenuItem>
                <MenuSeparator />
                <MenuItem onSelect={() => void logout()}>
                  <LogOut size={16} /> Esci
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </header>
        <main
          className={`dd-main${workspaceLayout ? ' dd-main--workspace' : ''}`}
          data-layout={workspaceLayout ? 'workspace' : 'page'}
          id="main-content"
        >
          <ModuleFieldHelpProvider pathname={location.pathname}>{children}</ModuleFieldHelpProvider>
        </main>
      </div>
      <CommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        navigation={visibleNavigation}
        hasPermission={hasPermission}
      />
      <ActivityDrawer open={activityOpen} onOpenChange={setActivityOpen} />
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function quickCreateLabel(id: string): string {
  const labels: Record<string, string> = {
    clients: 'Nuovo cliente',
    crm: 'Nuova opportunità',
    projects: 'Nuovo progetto',
    notes: 'Nuova nota',
    tasks: 'Nuova attività',
    billing: 'Nuova scadenza',
    expenses: 'Nuova spesa'
  };
  return labels[id] ?? 'Crea nuovo record';
}
